/*
 * Copyright (c) 2024 The Ontario Institute for Cancer Research. All rights reserved
 *
 * This program and the accompanying materials are made available under the terms of
 * the GNU Affero General Public License v3.0. You should have received a copy of the
 * GNU Affero General Public License along with this program.
 *  If not, see <http://www.gnu.org/licenses/>.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES
 * OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT
 * SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED
 * TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS;
 * OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER
 * IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

import { RestrictionRange } from '@overture-stack/lectern-dictionary';
import { invalid, valid } from '../../types/testResult';
import { isWithinRange } from '../../utils/isWithinRange';
import type { FieldRestrictionSingleValueTestFunction, FieldRestrictionTestFunction } from '../FieldRestrictionTest';
import { createFieldRestrictionTestForArrays } from './createFieldRestrictionTestForArrays';

const testRangeSingleValue: FieldRestrictionSingleValueTestFunction<RestrictionRange> = (rule, value) => {
	if (typeof value !== 'number') {
		// only apply range tests to numbers
		return valid();
	}

	if (isWithinRange(rule, value)) {
		return valid();
	}
	return invalid({ message: `The value must be within the range.`, rule });
};

const testRangeArray = createFieldRestrictionTestForArrays(
	testRangeSingleValue,
	`All values in the array must be within the range.`,
);

export const testRange: FieldRestrictionTestFunction<RestrictionRange> = (rule, value) =>
	Array.isArray(value) ? testRangeArray(rule, value) : testRangeSingleValue(rule, value);
